






































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import { AccountHolderIssueCardCriteria, AccountHolderIssueCardCriteriaResponse, CreateCardIssueCriteriaRequest } from "@/admin/types";
import { usePartnerCardIssueCriteriaStore } from "@/admin/store/partnerCardIssueCriteria";
import CardSettings from "@/shared/components/CardSettings.vue";
import { useEmployerStore } from "@/admin/store/employer";
import { useThemeStore } from "@/shared/store/theme";
import _ from "lodash";

interface UserTableItem {
  firstName: string;
  lastName: string;
  userEmail: string;
  userSsnLast4: string;
  issueCardCriteriaId: number;
  issueCardCustomizationId: number|null;
  dateEnabled: Date;
  status: "" | "Active" | "Inactive" | "Pending";
  synced: boolean;
}

@Component({
  components: {
    CardSettings
  },
})
export default class AccountHolderCardSettings extends Vue {
  @Prop({ required: true }) employerId: number;
  @Prop({ required: true }) partnerId: number;

  partnerCardIssueCriteriaStore = usePartnerCardIssueCriteriaStore();
  employerStore = useEmployerStore();
  themeStore = useThemeStore();

  AccountHolderIssueCardCriteriaResponse: AccountHolderIssueCardCriteriaResponse = null;
  ssn = null;
  userTableItems: null | UserTableItem[] = null;
  showCardSettingsEditForm = false;
  cardSettingsEditId = null;

  mounted () {
    this.loadCardIssueCriterias();
    this.partnerCardIssueCriteriaStore.loadAccountHoldersIssueCardCriterias(this.employerId);
    this.partnerCardIssueCriteriaStore.loadCardIssueCustomizationOptions();
  }

  @Watch("isLoaded")
  loadCardIssueCriterias () {
    if (this.isLoaded) {
      this.AccountHolderIssueCardCriteriaResponse = _.cloneDeep(this.partnerCardIssueCriteriaStore.cardIssueCriteriaForAccountHolders(this.employerId));
      if (this.AccountHolderIssueCardCriteriaResponse) {
        this.userTableItems = this.AccountHolderIssueCardCriteriaResponse.
          syncedUsers.map(this.mapSyncedUser).concat(
            this.AccountHolderIssueCardCriteriaResponse.unsyncedUsers.map(this.mapUnsyncedUser)
          );
      } else {
        this.userTableItems = null;
        this.ssn = null;
      }
    }
  }

  get isLoaded () {
    return this.partnerCardIssueCriteriaStore.isCardIssueCriteriaLoadedForAccountHolders(this.employerId);
  }

  get ssnValid (): boolean {
    return /^\d{3}-\d{2}-\d{4}$/.test(this.ssn);
  }

  get userTableFields () {
    return [
      { key: "firstName", label: "First Name" },
      { key: "lastName", label: "Last Name" },
      { key: "userEmail", label: "User Email" },
      { key: "userSsnLast4", label: "User Ssn Last 4 digits" },
      { key: "status", label: "Status" },
      { key: "actions", label: "Actions" }
    ];
  }

  mapUnsyncedUser (userConfig: AccountHolderIssueCardCriteria): UserTableItem {
    return {
      firstName: "User Not Found",
      lastName: "",
      userEmail: "",
      userSsnLast4: userConfig.ssnLast4,
      issueCardCriteriaId: userConfig.issueCardCriteriaId,
      issueCardCustomizationId: userConfig.issueCardCustomizationId,
      dateEnabled: userConfig.dateEnabled,
      status: this.partnerCardIssueCriteriaStore.getConfigStatus(userConfig),
      synced: false
    };
  }

  mapSyncedUser (userConfig: AccountHolderIssueCardCriteria): UserTableItem {
    return {
      firstName: userConfig.firstName,
      lastName: userConfig.lastName,
      userEmail: userConfig.email,
      userSsnLast4: userConfig.ssnLast4,
      issueCardCriteriaId: userConfig.issueCardCriteriaId,
      issueCardCustomizationId: userConfig.issueCardCustomizationId,
      dateEnabled: userConfig.dateEnabled,
      status: this.partnerCardIssueCriteriaStore.getConfigStatus(userConfig),
      synced: true
    };
  }

  canActivate (item: UserTableItem) {
    if (item === undefined || item.synced === false) {
      return false;
    }

    if (item.status === "Active" || item.status === "Pending") {
      return false;
    }

    return item.issueCardCustomizationId && item.issueCardCustomizationId >= 0;
  }

  askDeleteUserCardIssueCriteria (item: UserTableItem) {
    let userLabel = item.lastName ? `${item.firstName} ${item.lastName} ` : "";
    userLabel += `with last 4 SSN of ${item.userSsnLast4}`;
    if (
      confirm(`Are you sure you would like to remove card issuance for account holder ${userLabel}? Account holder will still receive a card if enabled at the employer or partner level.`)
    ) {
      this.deleteCardIssueCriteria(item.issueCardCriteriaId);
    }
  }

  deleteCardIssueCriteria (issueCardCriteriaId: number) {
    return this.partnerCardIssueCriteriaStore.deleteCardIssueCriteria(issueCardCriteriaId)
      .then(() => {
        this.reload();
      });
  }

  activateCardIssueCriteria (issueCardCriteriaId: number) {
    if (confirm("Are you sure you want to activate this criteria? Account holders will start getting issued cards immediately using the current criteria settings. If you are not sure, hit Cancel, and check the criteria settings before enabling.")) {
      return this.partnerCardIssueCriteriaStore.activateCardIssueCriteria(issueCardCriteriaId)
        .then(() => {
          this.reload();
        });
    }
  }

  addCardIssueCriteria () {
    const request: CreateCardIssueCriteriaRequest = {
      partnerId: this.partnerId,
      employerId: this.employerId,
      ssn: this.ssn
    };

    return this.partnerCardIssueCriteriaStore.createCardIssueCriteria(request)
      .then(response => {
        if (response.data && response.data.issueCardCriteriaId) {
          return response.data.issueCardCriteriaId;
        }
      })
      .then(id => {
        this.ssn = null;
        this.showEditCardSettingsModal(id);
      });
  }

  showEditCardSettingsModal (issueCardCriteriaId: number) {
    this.cardSettingsEditId = issueCardCriteriaId;
    this.showCardSettingsEditForm = true;
  }

  close () {
    this.showCardSettingsEditForm = false;
    this.reload();
  }

  reload () {
    this.partnerCardIssueCriteriaStore.reloadAccountHoldersIssueCardCriterias(this.employerId);
  }
}
